import { useQuery } from '@tanstack/react-query';
import { CostData } from '../types';
import { fetchCostDataFromApi, getCostData } from '../utils/dataAdapter';

interface UseCostDataParams {
  startDate: string;
  endDate: string;
  enabled?: boolean;
}

// 기본 API 호출 훅
export const useCostData = ({ startDate, endDate, enabled = true }: UseCostDataParams) => {
  return useQuery({
    queryKey: ['costData', startDate, endDate],
    queryFn: () => fetchCostDataFromApiWithDateRange(startDate, endDate),
    enabled: enabled && Boolean(startDate) && Boolean(endDate),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10분 (구 cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

// API 호출 후 날짜 범위 필터링
const fetchCostDataFromApiWithDateRange = async (startDate: string, endDate: string): Promise<CostData[]> => {
  const apiData = await fetchCostDataFromApi();
  
  // 날짜 범위 필터링
  return apiData.filter((item: CostData) => 
    item.date >= startDate && item.date <= endDate
  );
};

// 로컬 데이터 fallback을 위한 함수
export const useCostDataWithFallback = ({ startDate, endDate, enabled = true }: UseCostDataParams) => {
  console.log('🔄 useCostDataWithFallback 호출:', { startDate, endDate, enabled });
  
  return useQuery({
    queryKey: ['costDataWithFallback', startDate, endDate],
    queryFn: async (): Promise<CostData[]> => {
      console.log('🚀 Query 함수 실행 시작:', { startDate, endDate });
      
      try {
        console.log('📡 API 호출 시도...');
        const apiData = await fetchCostDataFromApi();
        console.log('✅ API 호출 성공:', apiData.length, '개 항목');
        
        // 날짜 범위 필터링
        const filteredData = apiData.filter((item: CostData) => 
          item.date >= startDate && item.date <= endDate
        );
        console.log('📅 날짜 필터링 완료:', filteredData.length, '개 항목');
        
        return filteredData;
      } catch (error) {
        console.error('❌ API 호출 실패, 로컬 데이터 사용:', error);
        
        // 로컬 데이터 사용
        const localData = getCostData() as CostData[];
        console.log('📁 로컬 데이터 로드:', localData.length, '개 항목');
        
        // 날짜 범위 필터링
        const filteredData = localData.filter((item: CostData) => 
          item.date >= startDate && item.date <= endDate
        );
        console.log('📅 로컬 데이터 필터링 완료:', filteredData.length, '개 항목');
        
        return filteredData;
      }
    },
    enabled: enabled && Boolean(startDate && endDate),
    staleTime: 1000 * 60 * 5, // 5분
    gcTime: 1000 * 60 * 10, // 10분
    retry: 1
  });
}; 